import React, { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import SelectSocialProfileOption, {
  SocialProfile,
} from '../SelectSocialProfileOption';
import BulkSelectLinks from './BulkSelectLinks';
import { PortfolioColumn } from './portfolioColumns';

type Props = {
  // The open portfolio; null when a search left none.
  column: PortfolioColumn | null;
  isSearching: boolean;
  selectedIds: ReadonlySet<string>;
  // Must be stable, so a memoised row re-renders only when its own state does.
  onRowClick: (id: SocialProfile['id']) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  // The scroller, which the picker resets to the top.
  panelRef: React.Ref<HTMLDivElement>;
  className?: string;
};

// The right column: the open portfolio's pages under a header pinned to the
// top of its own scroller.
const PortfolioPagesPanel: FC<Props> = ({
  column,
  isSearching,
  selectedIds,
  onRowClick,
  onSelectAll,
  onSelectNone,
  panelRef,
  className,
}) => {
  const { t } = useTranslation();
  const networkLabels: Partial<Record<number, string>> = {
    [TYPE.FACEBOOK]: t('newSocialMediaProfile.twoColumn.facebookPage'),
    [TYPE.INSTAGRAM]: t('newSocialMediaProfile.twoColumn.instagram'),
  };
  const columnLabel = column?.isOutside
    ? t('newSocialMediaProfile.oboTokenType.outsidePortfoliosHeading')
    : column?.label ?? '';

  return (
    <div
      ref={panelRef}
      data-cy="two-column-picker_pages-panel"
      className={clsx(
        'tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white',
        className,
      )}
    >
      {column ? (
        <>
          <div
            data-cy="two-column-picker_pages-header"
            className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-grayscale-10 tw-bg-grayscale-5 tw-px-4 tw-py-3"
          >
            <span className="tw-min-w-0 tw-truncate tw-text-md">
              <span className="tw-font-semibold tw-text-grayscale-180">
                {columnLabel}
              </span>
              <span className="tw-text-grayscale-100">
                {' · '}
                {t('newSocialMediaProfile.twoColumn.pagesCount', {
                  count: column.profiles.length,
                })}
              </span>
            </span>
            {column.connectableIds.length > 0 && (
              <BulkSelectLinks
                onSelectAll={onSelectAll}
                onSelectNone={onSelectNone}
                scopeLabel={columnLabel}
                dataCyPrefix="two-column-picker"
              />
            )}
          </div>
          {column.profiles.length > 0 ? (
            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
              {column.profiles.map((profile) => (
                <SelectSocialProfileOption
                  key={profile.id}
                  profile={profile}
                  subtitle={networkLabels[profile.type]}
                  isSelected={selectedIds.has(String(profile.id))}
                  onClick={onRowClick}
                />
              ))}
            </div>
          ) : (
            // A portfolio read only in part can come with no pages at all.
            <p
              className="tw-p-4 tw-text-md tw-text-grayscale-100"
              data-cy="two-column-picker_no-pages-loaded"
            >
              {t('newSocialMediaProfile.twoColumn.noPagesLoaded')}
            </p>
          )}
        </>
      ) : (
        isSearching && (
          <p
            className="tw-p-8 tw-text-center tw-text-md tw-text-grayscale-100"
            data-cy="two-column-picker_no-matches"
          >
            {t('newSocialMediaProfile.noProfilesMatchSearch')}
          </p>
        )
      )}
    </div>
  );
};

export default PortfolioPagesPanel;
